import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  ReportIssue,
  LightBox,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  AutoLogout,
} from '@backstage/core-components';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { createApp } from '@backstage/frontend-defaults';
import { convertLegacyAppOptions, convertLegacyAppRoot } from '@backstage/core-compat-api';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { ToolboxPage } from '@drodil/backstage-plugin-toolbox';
import { FluxRuntimePage } from '@weaveworksoss/backstage-plugin-flux';
import {
  configApiRef,
  gitlabAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { oidcAuthApiRef } from './apis/oidcAuthApi';
import authPlugin from '@backstage/plugin-auth';
import { pluginHeaderActionsApiCompatModule } from './pluginHeaderActionsApiCompat';

const convertedOptionsModule = convertLegacyAppOptions({
  apis,
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const environment = configApi.getOptionalString('auth.environment');

      if (environment === 'local' || environment === 'development' || environment === 'test') {
        return <SignInPage {...props} providers={['guest']} />;
      }

      const providers: Array<'guest' | 'custom' | {
        id: string;
        title: string;
        message: string;
        apiRef: any;
      }> = [
        {
          id: 'gitlab-custom-auth-provider',
          title: 'GitLab',
          message: 'Sign in using GitLab',
          apiRef: gitlabAuthApiRef,
        },
      ];

      const oidcConfigured = configApi.getOptionalConfig('auth.providers.oidc');
      if (oidcConfigured) {
        providers.push({
          id: 'oidc',
          title: 'JumpCloud',
          message: 'Sign in using JumpCloud',
          apiRef: oidcAuthApiRef,
        });
      }

      return <SignInPage {...props} providers={providers as any} />;
    },
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <LightBox />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/adrs" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/toolbox" element={<ToolboxPage />} />
    {/* <Route path="/open-dora" element={<OpenDoraPluginPage />} /> */}
    <Route path="/flux-runtime" element={<FluxRuntimePage />} />
  </FlatRoutes>
);

const convertedRootFeatures = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AutoLogout />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);

const app = createApp({
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  features: [
    pluginHeaderActionsApiCompatModule,
    convertedOptionsModule,
    ...convertedRootFeatures,
    authPlugin,
  ],
});

export default app.createRoot();
